import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  label?: string;
  options: MultiSelectOption[];
  value?: string[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  maxSelections?: number;
  className?: string;
}

export function MultiSelect({
  label,
  options,
  value = [],
  onChange,
  placeholder = 'Select options...',
  error,
  helperText,
  disabled,
  maxSelections,
  className,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Position the dropdown relative to the trigger and keep it in view
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownMaxHeight = 240; // matches max-h-60
      const openUpward = spaceBelow < dropdownMaxHeight + 8 && rect.top > dropdownMaxHeight;

      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        top: openUpward ? undefined : rect.bottom + 4,
        bottom: openUpward ? window.innerHeight - rect.top + 4 : undefined,
        zIndex: 9999,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (optionValue: string) => {
    if (disabled) return;

    const isSelected = value.includes(optionValue);
    let newValues: string[];

    if (isSelected) {
      newValues = value.filter((v) => v !== optionValue);
    } else {
      if (maxSelections && value.length >= maxSelections) {
        return;
      }
      newValues = [...value, optionValue];
    }

    onChange?.(newValues);
  };

  const removeOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange?.(value.filter((v) => v !== optionValue));
  };

  const selectedLabels = value.map(
    (v) => options.find((opt) => opt.value === v)?.label || v
  );

  const hasOptions = options.length > 0;
  // Auto-disable when there are no options so users aren't tricked into clicking an empty list
  const effectiveDisabled = disabled || !hasOptions;
  const effectivePlaceholder = !hasOptions ? 'No options available' : placeholder;

  return (
    <div className={clsx('w-full', className)} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
          {label}
        </label>
      )}
      <div
        ref={triggerRef}
        className={clsx(
          'relative min-h-[38px] rounded-md border px-3 py-1.5 text-sm shadow-sm dark:bg-gray-800',
          effectiveDisabled ? 'cursor-not-allowed bg-gray-50 dark:bg-gray-900' : 'cursor-pointer',
          error
            ? 'border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500'
            : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 dark:border-gray-700',
        )}
        onClick={() => !effectiveDisabled && setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1 pr-6">
          {value.length === 0 ? (
            <span className="text-gray-400 py-0.5 dark:text-gray-500">{effectivePlaceholder}</span>
          ) : (
            selectedLabels.map((label, index) => (
              <span
                key={value[index]}
                className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
              >
                {label}
                <button
                  type="button"
                  onClick={(e) => removeOption(value[index], e)}
                  className="text-blue-600 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100"
                >
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            ))
          )}
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <svg
            className={clsx('h-5 w-5 text-gray-400 transition-transform dark:text-gray-500', isOpen && 'rotate-180')}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-gray-800 dark:ring-gray-700"
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No options available
              </div>
            ) : (
              options.map((option) => {
                const isSelected = value.includes(option.value);
                const isDisabled =
                  option.disabled ||
                  Boolean(maxSelections && value.length >= maxSelections && !isSelected);

                return (
                  <div
                    key={option.value}
                    className={clsx(
                      'flex items-center px-3 py-2 text-sm cursor-pointer text-gray-900 dark:text-gray-100',
                      isSelected && 'bg-blue-50 dark:bg-blue-900/30',
                      isDisabled
                        ? 'text-gray-400 cursor-not-allowed dark:text-gray-500'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700',
                    )}
                    onClick={() => !isDisabled && toggleOption(option.value)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      readOnly
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 mr-2 dark:border-gray-600 dark:bg-gray-700"
                    />
                    {option.label}
                  </div>
                );
              })
            )}
          </div>,
          document.body,
        )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {!error && !hasOptions && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
          No options configured for this field.
        </p>
      )}
      {helperText && !error && hasOptions && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
